import { UserProfile, UserPortfolioData, CryptoError } from './lib/crypto/crypto-types.js';

export class IndexedDBService {
  private static instance: IndexedDBService;
  private db: IDBDatabase | null = null;
  private readonly dbName = 'CryptoPortfolioStorage';
  private readonly version = 1;

  static getInstance(): IndexedDBService {
    if (!IndexedDBService.instance) {
      IndexedDBService.instance = new IndexedDBService();
    }
    return IndexedDBService.instance;
  }

  /**
   * Initialize the IndexedDB database
   */
  async initialize(): Promise<void> {
    if (this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        reject(new CryptoError('Failed to open IndexedDB', 'DB_OPEN_ERROR'));
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Users object store
        if (!db.objectStoreNames.contains('users')) {
          const userStore = db.createObjectStore('users', { keyPath: 'id' });
          userStore.createIndex('username', 'username', { unique: true });
          userStore.createIndex('lastLogin', 'lastLogin');
        }

        // Portfolio data object store
        if (!db.objectStoreNames.contains('portfolios')) {
          const portfolioStore = db.createObjectStore('portfolios', { keyPath: 'userId' });
          portfolioStore.createIndex('lastModified', 'lastModified');
        }

        // Login attempts store for rate limiting
        if (!db.objectStoreNames.contains('loginAttempts')) {
          const attemptsStore = db.createObjectStore('loginAttempts', { keyPath: 'username' });
          attemptsStore.createIndex('timestamp', 'timestamp');
        }
      };
    });
  }

  /**
   * Store a user profile
   */
  async storeUser(userProfile: UserProfile): Promise<void> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['users'], 'readwrite');
      const store = transaction.objectStore('users');
      
      const request = store.put({
        ...userProfile,
        salt: Array.from(userProfile.salt), // Convert Uint8Array for storage
        wrappedMasterKey: Array.from(userProfile.wrappedMasterKey)
      });

      request.onerror = () => {
        reject(new CryptoError('Failed to store user', 'USER_STORE_ERROR'));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Get a user profile by username
   */
  async getUserByUsername(username: string): Promise<UserProfile | null> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['users'], 'readonly');
      const store = transaction.objectStore('users');
      const index = store.index('username');
      
      const request = index.get(username);

      request.onerror = () => {
        reject(new CryptoError('Failed to get user', 'USER_GET_ERROR'));
      };

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // Convert arrays back to Uint8Arrays
          resolve({
            ...result,
            salt: new Uint8Array(result.salt),
            wrappedMasterKey: new Uint8Array(result.wrappedMasterKey)
          });
        } else {
          resolve(null);
        }
      };
    });
  }

  /**
   * Get a user profile by ID
   */
  async getUserById(userId: string): Promise<UserProfile | null> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['users'], 'readonly');
      const store = transaction.objectStore('users');
      
      const request = store.get(userId);

      request.onerror = () => {
        reject(new CryptoError('Failed to get user', 'USER_GET_ERROR'));
      };

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            ...result,
            salt: new Uint8Array(result.salt),
            wrappedMasterKey: new Uint8Array(result.wrappedMasterKey)
          });
        } else {
          resolve(null);
        }
      };
    });
  }

  /**
   * Get all users sorted by activity
   */
  async getAllUsers(): Promise<UserProfile[]> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['users'], 'readonly');
      const store = transaction.objectStore('users');
      
      const request = store.getAll();

      request.onerror = () => {
        reject(new CryptoError('Failed to get users', 'USERS_GET_ERROR'));
      };

      request.onsuccess = () => {
        const users = request.result.map((user: any) => ({
          ...user,
          salt: new Uint8Array(user.salt),
          wrappedMasterKey: new Uint8Array(user.wrappedMasterKey)
        }));

        // Sort by activity (recent login + login count)
        users.sort((a, b) => {
          const aActivity = new Date(a.lastLogin).getTime() + (a.loginCount * 1000);
          const bActivity = new Date(b.lastLogin).getTime() + (b.loginCount * 1000);
          return bActivity - aActivity;
        });

        resolve(users);
      };
    });
  }

  /**
   * Update user login information
   */
  async updateUserLogin(userId: string): Promise<void> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    const user = await this.getUserById(userId);
    if (!user) {
      throw new CryptoError('User not found', 'USER_NOT_FOUND');
    }

    user.lastLogin = new Date().toISOString();
    user.loginCount += 1;

    await this.storeUser(user);
  }

  /**
   * Store portfolio data
   */
  async storePortfolio(portfolioData: UserPortfolioData): Promise<void> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['portfolios'], 'readwrite');
      const store = transaction.objectStore('portfolios');
      
      const request = store.put({
        ...portfolioData,
        encryptedPortfolio: {
          ciphertext: Array.from(portfolioData.encryptedPortfolio.ciphertext),
          iv: Array.from(portfolioData.encryptedPortfolio.iv)
        }
      });

      request.onerror = () => {
        reject(new CryptoError('Failed to store portfolio', 'PORTFOLIO_STORE_ERROR'));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Get portfolio data for a user
   */
  async getPortfolio(userId: string): Promise<UserPortfolioData | null> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['portfolios'], 'readonly');
      const store = transaction.objectStore('portfolios');
      
      const request = store.get(userId);

      request.onerror = () => {
        reject(new CryptoError('Failed to get portfolio', 'PORTFOLIO_GET_ERROR'));
      };

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            ...result,
            encryptedPortfolio: {
              ciphertext: new Uint8Array(result.encryptedPortfolio.ciphertext),
              iv: new Uint8Array(result.encryptedPortfolio.iv)
            }
          });
        } else {
          resolve(null);
        }
      };
    });
  }

  /**
   * Delete user and associated data
   */
  async deleteUser(userId: string): Promise<void> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['users', 'portfolios'], 'readwrite');
      
      const userStore = transaction.objectStore('users');
      const portfolioStore = transaction.objectStore('portfolios');
      
      userStore.delete(userId);
      portfolioStore.delete(userId);

      transaction.onerror = () => {
        reject(new CryptoError('Failed to delete user', 'USER_DELETE_ERROR'));
      };

      transaction.oncomplete = () => {
        resolve();
      };
    });
  }

  /**
   * Record login attempt for rate limiting
   */
  async recordLoginAttempt(username: string, success: boolean): Promise<void> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['loginAttempts'], 'readwrite');
      const store = transaction.objectStore('loginAttempts');
      
      const request = store.put({
        username,
        timestamp: Date.now(),
        success,
        attempts: 1
      });

      request.onerror = () => {
        reject(new CryptoError('Failed to record login attempt', 'LOGIN_ATTEMPT_ERROR'));
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  /**
   * Get recent login attempts for rate limiting
   */
  async getRecentLoginAttempts(username: string, timeWindow: number = 3600000): Promise<number> {
    await this.initialize();
    if (!this.db) throw new CryptoError('Database not initialized', 'DB_NOT_INITIALIZED');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['loginAttempts'], 'readonly');
      const store = transaction.objectStore('loginAttempts');
      
      const request = store.get(username);

      request.onerror = () => {
        reject(new CryptoError('Failed to get login attempts', 'LOGIN_ATTEMPTS_GET_ERROR'));
      };

      request.onsuccess = () => {
        const result = request.result;
        if (result && (Date.now() - result.timestamp) < timeWindow) {
          resolve(result.attempts || 0);
        } else {
          resolve(0);
        }
      };
    });
  }

  /**
   * Export user data for backup
   */
  async exportUserData(userId: string): Promise<{ user: UserProfile; portfolio: UserPortfolioData | null }> {
    const user = await this.getUserById(userId);
    if (!user) {
      throw new CryptoError('User not found', 'USER_NOT_FOUND');
    }

    const portfolio = await this.getPortfolio(userId);
    
    return {
      user,
      portfolio
    };
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}
